import axios, { type AxiosResponse } from 'axios'
import { authStore, clearAuth } from '~/stores/auth'
import { companyStore } from '~/stores/company'
import { isNetworkError, setOffline, setUnauthorized } from '~/stores/connection'
import type { ApiResponseType } from '~/types/api'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api'

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

/** Extrae el payload tipado de una respuesta estándar del backend. */
export async function apiData<T>(request: Promise<AxiosResponse<ApiResponseType<T>>>): Promise<T> {
  const { data } = await request
  if (data.status !== 'success' || data.data === undefined) {
    throw new Error(data.message || 'La respuesta de la API no contiene datos')
  }
  return data.data
}

api.interceptors.request.use(config => {
  // Con FormData manda el navegador: necesita poner él mismo el boundary del
  // multipart. Si se deja el 'application/json' por defecto, el cuerpo viaja
  // como multipart pero anunciado como JSON y el backend responde 422.
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }

  if (authStore.token) {
    config.headers.Authorization = `Bearer ${authStore.token}`
  }

  if (companyStore.id) {
    config.headers['X-Company-ID'] = companyStore.id
  }

  return config
})

api.interceptors.response.use(
  response => {
    setOffline(false)
    setUnauthorized(false)
    return response
  },
  error => {
    if (isNetworkError(error)) {
      setOffline(true)
    }

    const status = error.response?.status
    const url = error.config?.url ?? ''

    /**
     * 401 = no sabemos quién eres (token ausente, inválido o caducado). La
     * sesión guardada ya no sirve, así que se descarta y se manda al login.
     *
     * Antes esto sólo encendía el aviso de "sin acceso" y dejaba el token
     * muerto en localStorage: como el guard sólo mira que el token exista, se
     * seguía entrando al panel y todas las peticiones fallaban en silencio.
     * Para "estás dentro pero esto no es para ti" el backend responde 403.
     */
    if (status === 401) {
      const isLoginRequest = url.includes('/auth/login')
      const hadSession = !!authStore.token

      if (!isLoginRequest) {
        // Sólo hay algo que limpiar si había una sesión guardada. Un visitante
        // anónimo nunca tuvo una: para él un 401 es simplemente "esto no es
        // para ti", no un motivo para mandarlo a ningún sitio.
        if (hadSession) clearAuth()

        /**
         * Expulsar sólo desde el panel. La web pública (landing, páginas
         * publicadas en /p, formularios en /f) se navega sin cuenta: echar de
         * ahí a alguien que ni siquiera intentaba entrar sería absurdo.
         */
        const enPanel = window.location.pathname.startsWith('/admin')
        if (hadSession && enPanel) {
          // location.href y no el router: fuerza un arranque limpio y descarta
          // el estado en memoria de la sesión anterior.
          window.location.href = '/login'
        }
      }
      // En el login lo maneja el propio formulario ("Credenciales inválidas").
    }

    // 403: la sesión vale, pero falta permiso o empresa activa. Ni se cierra
    // sesión ni se redirige: la pantalla muestra "Sin acceso".
    if (status === 403) {
      setUnauthorized(true)
    }
    return Promise.reject(error)
  }
)

/**
 * Consulta al endpoint GraphQL de sólo lectura (`/api/graphql`).
 *
 * Va sobre el mismo `api`, así que hereda el `Authorization`, el `X-Company-ID`
 * y el manejo de 401/403 de los interceptores: una consulta GraphQL con la
 * sesión caducada expulsa al login igual que cualquier otra petición.
 *
 * No se añade un cliente GraphQL (Apollo, urql) a propósito: el panel se
 * estructura con `createResource` y meter un cliente con su propia caché y su
 * propio ciclo de vida chocaría con eso. Para leer datos, esto basta.
 *
 * GraphQL responde 200 aunque la consulta falle, con los errores en `errors`.
 * Aquí se convierten en una excepción para que el `catch` de siempre siga
 * sirviendo.
 */
export async function gql<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  const { data } = await api.post<{ data?: T; errors?: Array<{ message?: string }> }>('/graphql', { query, variables })

  if (data.errors?.length) {
    throw new Error(data.errors[0]?.message ?? 'Error en la consulta')
  }

  if (data.data === undefined) throw new Error('La consulta GraphQL no devolvió datos')
  return data.data
}

export default api
